import React from "react";
import { Destination, DestinationType, Organization } from "../common/ObjectTypes";
import { useTranslation } from 'react-i18next';
import ConnectionDestinationTable from "../destinations/ConnectionDestinationTable";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";
import { filterClasses } from "../common/tailwindClassnames";
import { Clear } from "../common/ProjectIcons";

type FilterOption = { label: string; value: string };

type StepThreeProps = {
    destinations: Destination[],
    existingDestPhase0: Destination[],
    existingDestPhase1: Destination[],
    existingDestPhase2: Destination[],
    existingDestPhase3: Destination[],
    onSelectExistingDestination: Function,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    organizations: Organization[],
    loadingOrganizations: boolean,
    globalSearchStr: string,
    onGlobalDestinationFilter: (value: string) => void,
    onChangeTypeFilter: Function,
    typeFilterValue: FilterOption | null,
    onChangeOwnerFilter: Function,
    ownerFilterValue: FilterOption | null,
    addExistingDest: boolean,
    updatingDestinations: boolean,
    onClickAddDestination: Function,
    onAddExistingDestinations: any,
    onViewDetails: Function,
    searchTerm?: string
}

const NewConnectionStepThree = ({
    destinations,
    destinationTypes,
    loadingDestinationTypes,
    organizations,
    loadingOrganizations,
    existingDestPhase0,
    existingDestPhase1,
    existingDestPhase2,
    existingDestPhase3,
    onSelectExistingDestination,
    globalSearchStr,
    onGlobalDestinationFilter,
    onChangeTypeFilter,
    typeFilterValue,
    onChangeOwnerFilter,
    ownerFilterValue,
    updatingDestinations,
    addExistingDest,
    onClickAddDestination,
    onAddExistingDestinations,
    onViewDetails,
    searchTerm = ""
}: StepThreeProps) => {
    const { t } = useTranslation();
    return (
        <div className="mt-8">
            <div className="flex flex-col w-full">
                <label className="text-off-black text-lg">{t("Select the destination where you'd like to see this data.")}</label>
                <div className="flex flex-col mt-8 mb-16">
                    {addExistingDest && <>
                        {/* Horizontal filter bar */}
                        <div className="flex flex-row gap-3 mb-3 flex-wrap">
                            {/* Name */}
                            <div className="flex flex-col gap-1">
                                <span className={filterClasses.label}>{t("Name")}</span>
                                <GlobalSearch onChangeGlobalSearch={onGlobalDestinationFilter} savedString={globalSearchStr} />
                            </div>
                            {/* Technology */}
                            <div className="flex flex-col gap-1">
                                <span className={filterClasses.label}>{t("Technology")}</span>
                                <SelectInput
                                    name="type__in"
                                    options={destinationTypes.map(d => ({ label: d.name, value: d.id }))}
                                    value={typeFilterValue}
                                    onChange={onChangeTypeFilter}
                                    isMulti={false}
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={false}
                                    isLoading={loadingDestinationTypes}
                                    className="w-48"
                                    variant="connectionFilter"
                                />
                            </div>
                            {/* Workspace */}
                            <div className="flex flex-col gap-1">
                                <span className={filterClasses.label}>{t("Workspace")}</span>
                                <SelectInput
                                    name="owner__in"
                                    options={organizations.map(org => ({ label: org.name, value: org.id }))}
                                    value={ownerFilterValue}
                                    onChange={onChangeOwnerFilter}
                                    isMulti={false}
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={false}
                                    isLoading={loadingOrganizations}
                                    className="w-48"
                                    variant="connectionFilter"
                                />
                            </div>
                        </div>
                        {/* Filter chips */}
                        {(globalSearchStr || typeFilterValue || ownerFilterValue) &&
                            <div className="flex flex-row flex-wrap gap-2 mb-3">
                                {globalSearchStr && <span className={filterClasses.tag}>
                                    {globalSearchStr}
                                    <span className="cursor-pointer text-dark-green" onClick={() => onGlobalDestinationFilter('')}><Clear /></span>
                                </span>}
                                {typeFilterValue && <span className={filterClasses.tag}>
                                    {typeFilterValue.label}
                                    <span className="cursor-pointer text-dark-green" onClick={() => onChangeTypeFilter(null)}><Clear /></span>
                                </span>}
                                {ownerFilterValue && <span className={filterClasses.tag}>
                                    {ownerFilterValue.label}
                                    <span className="cursor-pointer text-dark-green" onClick={() => onChangeOwnerFilter(null)}><Clear /></span>
                                </span>}
                            </div>
                        }
                    </>}
                    <ConnectionDestinationTable
                        newConnection={true}
                        destinations={destinations}
                        existingDestPhase0={existingDestPhase0}
                        existingDestPhase1={existingDestPhase1}
                        existingDestPhase2={existingDestPhase2}
                        existingDestPhase3={existingDestPhase3}
                        addExistingDestinations={addExistingDest}
                        onAddExistingDestinations={onAddExistingDestinations}
                        updatingDestinations={updatingDestinations}
                        onClickAddDestination={onClickAddDestination}
                        onViewDetails={onViewDetails}
                        onRemoveDestination={() => { }}
                        onSelectDestination={onSelectExistingDestination}
                        searchTerm={searchTerm}
                    />
                </div>
            </div>
        </div>
    );
};

export default NewConnectionStepThree;
